var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import styleUtils from '../../styles/styleUtils';
import theme from '../../styles/theme';
import { PickerOptionInputStateContext } from '../../utils/InputStateContext';
import { Icon, } from '../Icon';
import { PickerOption, } from '../Picker';
import { PickerContext } from '../Picker/PickerContext';
const imageSize = 60;
const maxDescriptionHeight = 30;
const additionalLabelStyles = css `
  padding-left: ${theme.spacing.base};
`;
const optionStyles = css `
  ${styleUtils.borderBox}
  align-items: center;
  column-gap: ${theme.spacing.x4};
  display: flex;
  padding-bottom: ${theme.spacing.x2};
  padding-left: ${theme.spacing.x2};
  padding-right: ${theme.spacing.x2};
  padding-top: ${theme.spacing.x2};
`;
const labelStyles = css `
  ${theme.typeStyles.titleSmallStyles}
  display: flex;
`;
const moreLessStyles = css `
  ${theme.typeStyles.bodyBoldStyles}
  align-self: flex-end;
  background-color: ${theme.colors.gray100};
  position: absolute;
  right: 0;
  text-align: right;
`;
const textContainerStyles = css `
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  row-gap: ${theme.spacing.base};
`;
const OptionContent = ({ additionalLabel, alt, description, iconType, label, src, }) => {
    const { isLoading, } = useContext(PickerContext);
    const { isDisabled = false, isSelected = false, } = useContext(PickerOptionInputStateContext);
    const descriptionRef = useRef(null);
    const [isDescriptionTruncated, setIsDescriptionTruncated,] = useState(false);
    const onUpdate = useCallback(() => {
        var _a;
        const descriptionHeight = ((_a = descriptionRef.current) === null || _a === void 0 ? void 0 : _a.offsetHeight) || 0;
        const isOverflowing = descriptionHeight > maxDescriptionHeight;
        setIsDescriptionTruncated(!isSelected && isOverflowing);
    }, [
        isSelected,
    ]);
    const imageStyles = useMemo(() => (css `
        height: ${styleUtils.rem(imageSize)};
        width: ${styleUtils.rem(imageSize)};

        ${description && css `
          align-self: flex-start;
        `}
        ${(isLoading || isDisabled) && css `
          opacity: ${styleUtils.disabledStateOpacity};
        `}
      `), [
        description,
        isDisabled,
        isLoading,
    ]);
    const descriptionStyles = useMemo(() => (css `
        ${theme.typeStyles.bodyExtraSmallStyles}
        
        white-space: normal;
        height: 100%;
        ${(isLoading || isDisabled) && css `
          color: ${theme.colors.textDisabled};
        `}
      `), [
        isDisabled,
        isLoading,
    ]);
    const labelTextStyles = useMemo(() => (css `
        flex-grow: 1;
        ${(isLoading || isDisabled) && css `
          color: ${theme.colors.textDisabled};
        `}
      `), [
        isDisabled,
        isLoading,
    ]);
    const additionalContentStyles = useMemo(() => (css `
        padding-right: ${theme.spacing.base};

        ${description && css `
          align-self: flex-start;
        `}
      `), [
        description,
    ]);
    const descriptionContainerStyles = useMemo(() => (css `
        ${theme.typeStyles.bodyExtraSmallStyles}

        ${isDescriptionTruncated && css `
          display: flex;
          height: 100%;
          max-height: ${styleUtils.rem(maxDescriptionHeight)};
          overflow: hidden;
          position: relative;
        `}
        ${(isLoading || isDisabled) && css `
          color: ${theme.colors.textDisabled};
        `}
      `), [
        isDescriptionTruncated,
        isDisabled,
        isLoading,
    ]);
    useEffect(() => {
        onUpdate();
    }, [
        onUpdate,
    ]);
    return (_jsxs("div", Object.assign({ css: optionStyles }, { children: [src
                && (_jsx("img", { alt: alt, css: imageStyles, src: src })), _jsxs("div", Object.assign({ css: textContainerStyles }, { children: [_jsxs("span", Object.assign({ css: labelStyles }, { children: [iconType
                                && (_jsx("div", Object.assign({ css: additionalContentStyles }, { children: _jsx(Icon, { size: "extraSmall", type: iconType }) }))), _jsx("span", Object.assign({ css: labelTextStyles }, { children: label })), _jsx("span", Object.assign({ css: additionalLabelStyles }, { children: additionalLabel }))] })), description
                        && (_jsxs("span", Object.assign({ css: descriptionContainerStyles }, { children: [_jsx("span", Object.assign({ css: descriptionStyles, ref: descriptionRef }, { children: description })), isDescriptionTruncated
                                    && (_jsx("span", Object.assign({ css: moreLessStyles }, { children: "...More" })))] })))] }))] })));
};
const LongTextPickerOption = (_a) => {
    var { additionalLabel, alt, description, iconType, label, src } = _a, pickerOptionProps = __rest(_a, ["additionalLabel", "alt", "description", "iconType", "label", "src"]);
    return (_jsx(PickerOption, Object.assign({}, pickerOptionProps, { children: _jsx(OptionContent, { additionalLabel: additionalLabel, alt: alt, description: description, iconType: iconType, label: label, src: src }) })));
};
const MemoizedLongTextPickerOption = memo(LongTextPickerOption);
export { MemoizedLongTextPickerOption as LongTextPickerOption };
// MC-1068 Temporary hack for Storybook.
export default LongTextPickerOption;
